import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../config/axios';
import { track } from '../utils/analytics';
import { useAuth } from './AuthContext';

// Shared favorites state so N hearts on a list page don't issue N GETs.
// The provider loads the current user's favorites ONCE (per login, per page
// load) and every FavoriteButton reads/toggles through it. Keys are
// `${entity_type}:${slug}` — entity_type is the plural route key the API
// canonicalizes on (spells/feats/...).
//
// The favorites API is per-user AND per-system (req.systemId via the axios
// ?system= interceptor). Switching systems triggers a full page reload in
// this app, which remounts the provider — no system-change refetch needed.
//
// The default value keeps useFavorites() safe without a provider (isolated
// component tests): nothing favorited, no-op toggle.
const favKey = (entityType, slug) => `${entityType}:${slug}`;

const FavoritesContext = createContext({
  loaded: false,
  isFavorited: () => false,
  toggleFavorite: async () => {},
});

export const useFavorites = () => useContext(FavoritesContext);

export function FavoritesProvider({ children }) {
  const { isAuthenticated } = useAuth();
  // Set of `${entity_type}:${slug}` keys.
  const [favorites, setFavorites] = useState(() => new Set());
  const [loaded, setLoaded] = useState(false);

  // Hydrate LAZILY: on the first heart that renders, not on sign-in. Half the
  // site's pages show no heart (home, account, campaigns, characters, the
  // generators) and every signed-in boot used to pay this request anyway
  // (API-call budget, 2026-09-07). `wanted` is the one-shot trigger; the
  // favorites page primes the set from its own list instead of asking twice.
  const [wanted, setWanted] = useState(false);
  const ensureLoaded = useCallback(() => setWanted(true), []);
  const primeFavorites = useCallback((list) => {
    setFavorites(new Set((Array.isArray(list) ? list : []).map((f) => favKey(f.entity_type, f.slug))));
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setFavorites(new Set());
      setLoaded(false);
      return undefined;
    }
    if (!wanted || loaded) return undefined;
    let cancelled = false;
    apiClient
      .get('/api/favorites')
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setFavorites(new Set(list.map((f) => favKey(f.entity_type, f.slug))));
        setLoaded(true);
      })
      .catch(() => {
        /* favorite state is decorative — never break the page */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, wanted, loaded]);

  const isFavorited = useCallback(
    (entityType, slug) => favorites.has(favKey(entityType, slug)),
    [favorites]
  );

  // Optimistic toggle: flip the set immediately, POST/DELETE, revert on
  // failure. Swallows errors — hearts are decorative.
  const toggleFavorite = useCallback(
    async (entityType, slug) => {
      const key = favKey(entityType, slug);
      const adding = !favorites.has(key);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (adding) next.add(key);
        else next.delete(key);
        return next;
      });
      try {
        if (adding) {
          await apiClient.post('/api/favorites', { entity_type: entityType, slug });
        } else {
          // DELETE rides the query string — the API accepts body or query,
          // and axios DELETE bodies are awkward.
          await apiClient.delete('/api/favorites', { params: { entity_type: entityType, slug } });
        }
        track('favourite', { family: entityType, on: adding });
      } catch (err) {
        setFavorites((prev) => {
          const next = new Set(prev);
          if (adding) next.delete(key);
          else next.add(key);
          return next;
        });
      }
    },
    [favorites]
  );

  return (
    <FavoritesContext.Provider value={{ loaded, isFavorited, toggleFavorite, ensureLoaded, primeFavorites }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export default FavoritesContext;
